import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Check, Loader2, CalendarClock } from "lucide-react";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Review Your Salesforce Commercials | Contact SaaSed" },
      { name: "description", content: "Get in touch with our specialist procurement team for a review of your upcoming Salesforce SELA or renewal, independent of Salesforce." },
      { property: "og:title", content: "Review Your Salesforce Commercials | Contact SaaSed" },
      { property: "og:description", content: "Get in touch with our specialist procurement team for a review of your upcoming Salesforce SELA or renewal, independent of Salesforce." },
      { name: "twitter:description", content: "Get in touch with our specialist procurement team for a review of your upcoming Salesforce SELA or renewal, independent of Salesforce." },
      { property: "og:url", content: "https://saased.co/contact" },
    ],
    links: [{ rel: "canonical", href: "https://saased.co/contact" }],
  }),
  component: ContactPage,
});



const BOOKING_URL = "https://calendar.app.google/xbFxKCkdfWqfTr2y6";



function ContactPage() {
  return (
    <>
      <ScheduleSection />
      <FormSection />
    </>
  );
}


function ScheduleSection() {
  return (
    <section className="bg-secondary/40 py-20 md:py-28">
      <div className="container-x">
        <div className="flex flex-col items-center text-center">
          <h1 className="text-2xl md:text-4xl">Schedule a Complimentary Salesforce Audit</h1>
          <div className="mt-6 h-[3px] w-28 bg-primary" />
        </div>

        <div className="mx-auto mt-14 max-w-4xl overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
          <div className="flex flex-col items-center gap-6 px-6 py-16 text-center md:py-20">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <CalendarClock size={26} />
            </span>
            <h3 className="text-2xl md:text-3xl">Book a 30-minute call with Anders</h3>
            <p className="max-w-md text-muted-foreground">
              Pick a time that works for you. The call is free, no obligation —
              we'll walk through your Salesforce contract and where the savings likely sit.
            </p>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-primary-foreground transition hover:opacity-90 font-sans text-sm font-medium uppercase tracking-[0.15em]"
            >
              <CalendarClock size={18} />
              Open booking calendar
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

const WEB3FORMS_ACCESS_KEY = "435b491a-5643-4573-ad25-d0fbc33377ec";

function FormSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const fd = new FormData(e.currentTarget);
    const firstName = String(fd.get("first_name") ?? "").trim();
    const lastName = String(fd.get("last_name") ?? "").trim();
    fd.append("subject", `New contact form submission from ${`${firstName} ${lastName}`.trim()}`);
    fd.append("from_name", "SaaSed Contact Form");

    // Fire-and-forget Mailchimp subscription with "Website contact us submission form" tag.
    // Uses JSONP-style endpoint via no-cors to avoid CORS errors; does not block or affect
    // the primary Web3Forms email notification.
    const email = String(fd.get("email") ?? "").trim();
    if (email) {
      try {
        const company = String(fd.get("company") ?? "").trim();
        const jobTitle = String(fd.get("job_title") ?? "").trim();
        const phone = String(fd.get("phone") ?? "").trim();
        const mcParams = new URLSearchParams();
        mcParams.set("EMAIL", email);
        if (firstName) mcParams.set("FNAME", firstName);
        if (lastName) mcParams.set("LNAME", lastName);
        if (company) mcParams.set("COMPANY", company);
        if (jobTitle) mcParams.set("MMERGE9", jobTitle);
        if (phone) mcParams.set("PHONE", phone);
        mcParams.set("tags", "962"); // "Website contact us submission form"
        mcParams.set("b_b0b56a1a3a62f7fdadb7fe265_51a44aab1c", ""); // honeypot

        // Mailchimp JSONP endpoint (/post-json) — send merge fields via query
        // string and inject a <script> tag. Bypasses CORS entirely and is the
        // officially supported AJAX method for hosted embedded forms.
        const callbackName = `mc_cb_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
        const mcUrl =
          "https://saased.us22.list-manage.com/subscribe/post-json?u=b0b56a1a3a62f7fdadb7fe265&id=51a44aab1c&f_id=00aec2e1f0&" +
          mcParams.toString() +
          `&c=${callbackName}`;

        const script = document.createElement("script");
        script.src = mcUrl;
        (window as unknown as Record<string, unknown>)[callbackName] = (
          response: unknown,
        ) => {
          console.info("Mailchimp response:", response);
          delete (window as unknown as Record<string, unknown>)[callbackName];
          script.remove();
        };
        script.onerror = () => {
          delete (window as unknown as Record<string, unknown>)[callbackName];
          script.remove();
        };
        document.body.appendChild(script);
      } catch {
        // Ignore Mailchimp errors — must not affect primary submission
      }
    }

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: fd,
      });
      let json: { success?: boolean; message?: string } = {};
      try {
        json = await res.json();
      } catch {
        // Non-JSON response — fall through to status check
      }
      if (!res.ok || !json.success) {
        // Web3Forms notification failed, but the user's data was already sent to
        // Mailchimp above. Log for diagnostics and still show the success screen
        // so the user is never shown a generic error for a submission we captured.
        console.warn("Web3Forms notification failed:", json.message || res.status);
      }
      setSubmitted(true);
    } catch (err) {
      // Network error reaching Web3Forms. Mailchimp fire-and-forget was already
      // dispatched, so treat the submission as captured rather than surfacing a
      // generic failure page.
      console.warn("Web3Forms request error:", err);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="bg-secondary/40 pb-24">
      <div className="container-x">
        <h2 className="mx-auto max-w-3xl text-center text-2xl md:text-4xl" style={{ marginInline: "auto" }}>
          Have a question? Send us a message and we'll get back to you shortly.
        </h2>

        <div className="mx-auto mt-12 max-w-3xl">
          {submitted ? (
            <div className="flex flex-col items-center gap-4 rounded-3xl border border-border bg-card p-10 text-center shadow-soft">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check size={22} />
              </span>
              <h3 className="text-3xl">Thank you.</h3>
              <p className="text-muted-foreground">We've received your details. Expect a reply within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <input type="hidden" name="access_key" value={WEB3FORMS_ACCESS_KEY} />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FieldLine name="first_name" placeholder="First Name" required />
                <FieldLine name="last_name" placeholder="Last Name" required />
              </div>
              <FieldLine name="email" type="email" placeholder="Email address" required />
              <FieldLine name="company" placeholder="Business Name" required />
              <FieldLine name="job_title" placeholder="Job Title" />
              <FieldLine name="phone" placeholder="Phone Number" />
              <textarea
                name="message"
                rows={6}
                placeholder="Any additional information we should know?"
                className="w-full resize-none rounded-2xl border border-primary/40 bg-card px-5 py-4 text-base outline-none transition focus:border-foreground"
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex justify-center pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-10 py-3.5 text-primary-foreground shadow-soft transition-transform hover:-translate-y-0.5 disabled:opacity-60 font-sans text-sm font-medium uppercase tracking-[0.15em]"
                >
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  {submitting ? "Sending…" : "Submit"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function FieldLine({
  name, placeholder, type = "text", required,
}: { name: string; placeholder: string; type?: string; required?: boolean }) {
  return (
    <input
      name={name}
      type={type}
      required={required}
      placeholder={placeholder}
      className="w-full rounded-2xl border border-primary/40 bg-card px-5 py-4 text-base outline-none transition focus:border-foreground"
    />
  );
}

